import { supabase } from '../../lib/supabase';
import React from 'react';
import { Target, Sparkles, User } from 'lucide-react';
import { getActiveFocusCopy, getSuggestedFocusCopy, getInsightCopy } from './focusCopy';
import { loadUserMemory, refreshUserMemory } from './memoryEngine';
import { analyzeAndEnrich, enrichCardWithCrossInsight } from './crossReasoner';

// Development-only logging
const devLog = (...args) => {
  if (process.env.NODE_ENV === 'development') console.log('[FocusEngine]', ...args);
};

/**
 * Focus Card Schema
 * @typedef {Object} FocusCard
 * @property {string} id - Unique card identifier
 * @property {'active-focus'|'focus-suggestion'|'insight'} type - Card type
 * @property {string} title - Card title
 * @property {string} description - Card description
 * @property {string} ctaLabel - Call-to-action label
 * @property {Function} ctaAction - Call-to-action handler function
 * @property {string} gradient - Tailwind gradient classes
 * @property {React.ReactElement} icon - Icon component
 */

/**
 * Helper function to check if text contains verbs
 */
function containsVerbs(text) {
  if (!text) return false;
  // Common verb patterns (simplified check)
  const verbPatterns = /\b(?:is|are|was|were|has|have|had|do|does|did|will|would|should|could|can|may|might|make|makes|made|get|gets|got|go|goes|went|come|comes|came|take|takes|took|give|gives|gave|see|sees|saw|know|knows|knew|think|thinks|thought|find|finds|found|work|works|worked|use|uses|used|want|wants|wanted|need|needs|needed|try|tries|tried|call|calls|called|ask|asks|asked|tell|tells|told|help|helps|helped|show|shows|showed|play|plays|played|run|runs|ran|move|moves|moved|live|lives|lived|believe|believes|believed|bring|brings|brought|happen|happens|happened|write|writes|wrote|provide|provides|provided|sit|sits|sat|stand|stands|stood|lose|loses|lost|pay|pays|paid|meet|meets|met|include|includes|included|continue|continues|continued|set|sets|set|learn|learns|learned|change|changes|changed|lead|leads|led|understand|understands|understood|watch|watches|watched|follow|follows|followed|stop|stops|stopped|create|creates|created|speak|speaks|spoke|read|reads|read|allow|allows|allowed|add|adds|added|spend|spends|spent|grow|grows|grew|open|opens|opened|walk|walks|walked|win|wins|won|offer|offers|offered|remember|remembers|remembered|love|loves|loved|consider|considers|considered|appear|appears|appeared|buy|buys|bought|wait|waits|waited|serve|serves|served|die|dies|died|send|sends|sent|build|builds|built|stay|stays|stayed|fall|falls|fell|cut|cuts|cut|reach|reaches|reached|kill|kills|killed|raise|raises|raised|pass|passes|passed|sell|sells|sold|decide|decides|decided|return|returns|returned|explain|explains|explained|develop|develops|developed|carry|carries|carried|break|breaks|broke|receive|receives|received|agree|agrees|agreed|support|supports|supported|hit|hits|hit|produce|produces|produced|eat|eats|ate|cover|covers|covered|catch|catches|caught|draw|draws|drew|choose|chooses|chose|fight|fights|fought|push|pushes|pushed|deliver|delivers|delivered|pull|pulls|pulled|refer|refers|referred|drive|drives|drove|remain|remains|remained|suggest|suggests|suggested|require|requires|required|report|reports|reported|shoot|shoots|shot|leave|leaves|left|base|bases|based|apply|applies|applied|save|saves|saved|describe|describes|described|improve|improves|improved|thank|thanks|thanked|accept|accepts|accepted|realize|realizes|realized|prepare|prepares|prepared|join|joins|joined|share|shares|shared|reduce|reduces|reduced|expect|expects|expected|manage|manages|managed|discover|discovers|discovered|examine|examines|examined|design|designs|designed|protect|protects|protected|recognize|recognizes|recognized|remove|removes|removed|complete|completes|completed|claim|claims|claimed|warn|warns|warned|fail|fails|failed|arrive|arrives|arrived|survive|survives|survived|obtain|obtains|obtained|operate|operates|operated|achieve|achieves|achieved|select|selects|selected|affect|affects|affected|indicate|indicates|indicated|involve|involves|involved|result|results|resulted|establish|establishes|established|determine|determines|determined|represent|represents|represented|maintain|maintains|maintained|publish|publishes|published|observe|observes|observed|replace|replaces|replaced|encourage|encourages|encouraged|prevent|prevents|prevented|assume|assumes|assumed|introduce|introduces|introduced|prove|proves|proved|identify|identifies|identified|imagine|imagines|imagined|respond|responds|responded|tend|tends|tended|promote|promotes|promoted|demand|demands|demanded|examine|examines|examined|intend|intends|intended|attend|attends|attended|expand|expands|expanded|seek|seeks|sought|acquire|acquires|acquired|enable|enables|enabled|enhance|enhances|enhanced|ensure|ensures|ensured|examine|examines|examined|execute|executes|executed|generate|generates|generated|implement|implements|implemented|indicate|indicates|indicated|investigate|investigates|investigated|participate|participates|participated|purchase|purchases|purchased|pursue|pursues|pursued|recognize|recognizes|recognized|recommend|recommends|recommended|transform|transforms|transformed|utilize|utilizes|utilized|validate|validates|validated|analyze|analyzes|analyzed|anticipate|anticipates|anticipated|appreciate|appreciates|appreciated|communicate|communicates|communicated|concentrate|concentrates|concentrated|contribute|contributes|contributed|demonstrate|demonstrates|demonstrated|distinguish|distinguishes|distinguished|elaborate|elaborates|elaborated|emphasize|emphasizes|emphasized|evaluate|evaluates|evaluated|facilitate|facilitates|facilitated|illustrate|illustrates|illustrated|incorporate|incorporates|incorporated|investigate|investigates|investigated|manipulate|manipulates|manipulated|negotiate|negotiates|negotiated|organize|organizes|organized|prioritize|prioritizes|prioritized|recognize|recognizes|recognized|reinforce|reinforces|reinforced|substantiate|substantiates|substantiated|synthesize|synthesizes|synthesized|transform|transforms|transformed|utilize|utilizes|utilized|validate|validates|validated)\b/i;
  return verbPatterns.test(text.toLowerCase());
}

/**
 * Helper function to check if text contains priority verbs
 */
function containsPriorityVerbs(text) {
  if (!text) return false;
  const priorityVerbs = /\b(?:plan|organize|create|write|fix|solve|help|think)\b/i;
  return priorityVerbs.test(text.toLowerCase());
}

/**
 * Pipeline 1: Active Focus Detection
 * Finds the most recently updated conversation with user messages
 * Tuned: Stricter filtering rules with priority verb scoring
 */
async function getActiveFocusCard(userId, navigateFn) {
  try {
    console.log('[FocusEngine] Active pipeline start', { userId });
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const fortyEightHoursAgo = new Date(now.getTime() - 48 * 60 * 60 * 1000);
    
    // Get most recently updated conversations for this user
    const { data: conversations, error: convError } = await supabase
      .schema('public')
      .from('conversations')
      .select('id, title, updated_at')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(30); // Increased to allow for filtering

    if (convError) {
      console.error('Focus engine error pipeline 1 (conversations query):', convError);
      return null;
    }

    console.log('[FocusEngine] Active conversations found', conversations?.length);

    if (!conversations || conversations.length === 0) {
      devLog('Active: No conversations found');
      return null;
    }

    // Step 1: Load messages for all conversations and apply basic filters
    const candidateConvs = [];
    
    for (const conv of conversations) {
      const { data: messages, error: msgError } = await supabase
        .from('chat_messages')
        .select('content, created_at, role')
        .eq('conversation_id', conv.id)
        .order('created_at', { ascending: false });

      if (msgError) {
        devLog(`Active: Error loading messages for conv ${conv.id}:`, msgError);
        continue;
      }

      if (!messages || messages.length === 0) {
        devLog(`Active: Skipping conv ${conv.id} - no messages`);
        continue;
      }

      // Get last user message
      const userMessages = messages.filter(m => m.role === 'user');
      if (userMessages.length === 0) {
        devLog(`Active: Skipping conv ${conv.id} - no user messages`);
        continue;
      }

      const lastUserMessage = userMessages[0];
      const lastMessageDate = new Date(lastUserMessage.created_at);
      const messageWords = lastUserMessage.content?.trim().split(/\s+/).filter(w => w.length > 0) || [];
      const title = (conv.title || '').trim();
      
      // Filter 2: Exclude if last message is fewer than 4 words
      if (messageWords.length < 4) {
        devLog(`Active: Excluding conv ${conv.id} - last message too short (${messageWords.length} words, need 4+)`);
        continue;
      }

      // Filter 3: Exclude if title includes "Test", "New Chat", or is blank
      const titleLower = title.toLowerCase();
      if (titleLower.includes('test') || titleLower.includes('new chat') || title === '') {
        devLog(`Active: Excluding conv ${conv.id} - invalid title: "${title}"`);
        continue;
      }

      // Store candidate with metadata
      candidateConvs.push({
        conv,
        lastUserMessage,
        lastMessageDate,
        messageWords,
        title,
        updatedAt: new Date(conv.updated_at)
      });
    }

    if (candidateConvs.length === 0) {
      devLog('Active: No conversations passed basic filters (word count, title)');
      return null;
    }

    devLog(`Active: ${candidateConvs.length} conversations passed basic filters`);

    // Filter 1: Exclude conversations where last user message is older than 7 days
    // UNLESS there is no newer conversation with a valid last message
    const recentConvs = candidateConvs.filter(c => c.lastMessageDate > sevenDaysAgo);
    
    let finalCandidates;
    if (recentConvs.length > 0) {
      // We have recent conversations, so exclude old ones
      finalCandidates = recentConvs;
      const excludedCount = candidateConvs.length - recentConvs.length;
      if (excludedCount > 0) {
        devLog(`Active: Excluding ${excludedCount} conversations with last message older than 7 days (found ${recentConvs.length} recent ones)`);
      }
    } else {
      // No recent conversations, keep all candidates
      finalCandidates = candidateConvs;
      devLog(`Active: No conversations with last message within 7 days, keeping all ${candidateConvs.length} candidates`);
    }

    if (finalCandidates.length === 0) {
      devLog('Active: No conversations passed all filters');
      return null;
    }

    devLog(`Active: ${finalCandidates.length} conversations passed all filters, scoring...`);

    // Score remaining conversations
    const scoredConvs = finalCandidates.map(candidate => {
      let score = 0;
      const { conv, lastUserMessage, updatedAt, title } = candidate;
      const lastMessageText = lastUserMessage.content || '';

      // Scoring rule 4: +3 if last message contains priority verbs
      if (containsPriorityVerbs(lastMessageText)) {
        score += 3;
        devLog(`Active: Conv ${conv.id} +3 for priority verbs in last message`);
      }

      // Scoring rule 5: +2 if conversation was updated in last 48 hours
      if (updatedAt > fortyEightHoursAgo) {
        score += 2;
        devLog(`Active: Conv ${conv.id} +2 for updated in last 48 hours`);
      }

      devLog(`Active: Conv ${conv.id} final score: ${score} (title: "${title}", lastMsg: "${lastMessageText.substring(0, 50)}...")`);

      return {
        ...candidate,
        score
      };
    });

    // Sort by score (descending), then by updated_at (descending) for ties
    scoredConvs.sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      // Rule 6: If scores tie, pick most recently updated
      return b.updatedAt - a.updatedAt;
    });

    const bestCandidate = scoredConvs[0];
    devLog(`Active: Selected conv ${bestCandidate.conv.id} with score ${bestCandidate.score} (updated: ${bestCandidate.updatedAt.toISOString()})`);

    const { conv, lastUserMessage, messagesThisWeek, updatedAt } = bestCandidate;
    const { title, description } = getActiveFocusCopy(conv, lastUserMessage);

    // Generate microInsight for Active Focus
    const hoursSinceUpdate = Math.floor((now - updatedAt) / (60 * 60 * 1000));
    let microInsight;
    if (hoursSinceUpdate < 24) {
      microInsight = 'You touched this today. Momentum is still warm.';
    } else if (hoursSinceUpdate < 48) {
      microInsight = 'You were here recently. It is a good time to finish what you started.';
    } else if (messagesThisWeek >= 2) {
      microInsight = 'This has been one of your most active threads this week.';
    } else {
      microInsight = 'This is an unfinished thread. You can either close it out or start something cleaner.';
    }

    const card = {
      id: `active-focus-${conv.id}`,
      type: 'active-focus',
      title,
      description,
      microInsight,
      ctaLabel: 'Jump back into this',
      ctaAction: () => {
        if (navigateFn) {
          navigateFn(`/ai-chat/${conv.id}`);
        } else {
          window.location.href = `/ai-chat/${conv.id}`;
        }
      },
      gradient: 'from-blue-400 to-purple-400',
      icon: React.createElement(Target, { size: 16, className: 'text-purple-500' }),
    };
    devLog('Active card:', card);
    return card;
  } catch (error) {
    console.error('[FocusEngine] Active pipeline error', error);
    return null;
  }
}

/**
 * Pipeline 2: Focus Suggestion Engine
 * Analyzes uploads by channel to suggest what to focus on next
 * Tuned: Intelligent scoring with velocity, clustering, novelty, and dominance rules
 */
async function getSuggestedFocusCard(userId, navigateFn) {
  try {
    console.log('[FocusEngine] Suggested pipeline start', { userId });
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const threeDaysAgo = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
    const twoHours = 2 * 60 * 60 * 1000;

    // Query instagram_content for channel data with timestamps
    const { data: instagramData, error: instagramError } = await supabase
      .schema('public')
      .from('instagram_content')
      .select('channel, created_at')
      .eq('user_id', userId);

    if (instagramError) {
      console.error('Focus engine error pipeline 2 (instagram_content query):', instagramError);
    }

    // Query deepsearch.items for channel data with timestamps
    const { data: deepsearchData, error: deepsearchError } = await supabase
      .schema('deepsearch')
      .from('items')
      .select('meta, created_at')
      .eq('user_id', userId);

    if (deepsearchError) {
      console.error('Focus engine error pipeline 2 (deepsearch.items query):', deepsearchError);
    }

    // Collect all items with timestamps for clustering analysis
    const allItems = [];
    
    // Process instagram_content
    if (instagramData) {
      instagramData.forEach((item) => {
        const channel = item.channel || 'Instagram';
        allItems.push({
          channel,
          createdAt: new Date(item.created_at)
        });
      });
    }

    // Process deepsearch.items
    if (deepsearchData) {
      deepsearchData.forEach((item) => {
        const channel = item.meta?.channel || null;
        if (channel) {
          allItems.push({
            channel,
            createdAt: new Date(item.created_at)
          });
        }
      });
    }

    // If no items at all, return "Seed Swirl" card
    if (allItems.length === 0) {
      devLog('Suggested: No items found, returning seed card');
      const copy = getSuggestedFocusCopy(null, {});
      const card = {
        id: 'focus-suggestion-seed',
        type: 'focus-suggestion',
        title: copy.title,
        description: copy.description,
        microInsight: 'No content saved yet. Start by uploading a few items.',
        ctaLabel: 'Open Swirlspace',
        ctaAction: () => {
          if (navigateFn) {
            navigateFn('/');
          } else {
            window.location.href = '/';
          }
        },
        gradient: 'from-purple-400 to-pink-400',
        icon: React.createElement(Sparkles, { size: 16, className: 'text-purple-500' }),
      };
      devLog('Suggested card:', card);
      return card;
    }

    // Aggregate by channel with detailed time windows
    const channelData = {};
    
    for (const item of allItems) {
      const { channel, createdAt } = item;
      if (!channelData[channel]) {
        channelData[channel] = {
          items: [],
          total: 0,
          last_24h: 0,
          last_3d: 0,
          last_7d: 0,
          last_14d: 0
        };
      }
      channelData[channel].items.push(createdAt);
      channelData[channel].total++;
      
      if (createdAt > oneDayAgo) channelData[channel].last_24h++;
      if (createdAt > threeDaysAgo) channelData[channel].last_3d++;
      if (createdAt > sevenDaysAgo) channelData[channel].last_7d++;
      if (createdAt > fourteenDaysAgo) channelData[channel].last_14d++;
    }

    // Calculate scores for each channel
    const channelScores = [];
    
    for (const [channel, data] of Object.entries(channelData)) {
      let score = 0;
      const breakdown = {
        channel,
        rawCounts: {
          total: data.total,
          last_24h: data.last_24h,
          last_3d: data.last_3d,
          last_7d: data.last_7d,
          last_14d: data.last_14d
        },
        velocityBonus: 0,
        clusterBonus: 0,
        noveltyBonus: 0,
        stalePenalty: 0,
        finalScore: 0
      };

      // 1. Activity velocity scoring
      if (data.last_24h > 0) {
        score += 5;
        breakdown.velocityBonus += 5;
        devLog(`Suggested: ${channel} +5 for last_24h > 0 (${data.last_24h} items)`);
      }
      if (data.last_3d > 2) {
        score += 3;
        breakdown.velocityBonus += 3;
        devLog(`Suggested: ${channel} +3 for last_3d > 2 (${data.last_3d} items)`);
      }
      if (data.last_7d > 5) {
        score += 1;
        breakdown.velocityBonus += 1;
        devLog(`Suggested: ${channel} +1 for last_7d > 5 (${data.last_7d} items)`);
      }

      // 2. Session clustering: Check for 3+ items within 2 hour windows
      const sortedItems = [...data.items].sort((a, b) => a - b);
      let hasHotCluster = false;
      
      for (let i = 0; i < sortedItems.length - 2; i++) {
        const windowStart = sortedItems[i];
        const windowEnd = new Date(windowStart.getTime() + twoHours);
        let itemsInWindow = 1; // Count the starting item
        
        for (let j = i + 1; j < sortedItems.length; j++) {
          if (sortedItems[j] <= windowEnd) {
            itemsInWindow++;
          } else {
            break;
          }
        }
        
        if (itemsInWindow >= 3) {
          hasHotCluster = true;
          break;
        }
      }
      
      if (hasHotCluster) {
        score += 4;
        breakdown.clusterBonus = 4;
        devLog(`Suggested: ${channel} +4 for hot cluster (3+ items within 2 hours)`);
      }

      // 3. Deemphasize stale channels
      if (data.last_14d === 0) {
        score -= 4;
        breakdown.stalePenalty = -4;
        devLog(`Suggested: ${channel} -4 for stale (no activity in last 14 days)`);
      }

      // 4. Personal novelty logic
      if (data.total < 3 && data.last_3d > 0) {
        score += 6;
        breakdown.noveltyBonus = 6;
        devLog(`Suggested: ${channel} +6 for novelty (${data.total} lifetime items, ${data.last_3d} in last 3 days)`);
      }

      breakdown.finalScore = score;
      channelScores.push({
        channel,
        score,
        breakdown
      });
    }

    // Log detailed score breakdown for each channel
    devLog('Suggested: Per-channel score breakdown:', channelScores.map(c => ({
      channel: c.channel,
      rawCounts: c.breakdown.rawCounts,
      velocityBonus: c.breakdown.velocityBonus,
      clusterBonus: c.breakdown.clusterBonus,
      noveltyBonus: c.breakdown.noveltyBonus,
      stalePenalty: c.breakdown.stalePenalty,
      finalScore: c.score
    })));

    // Sort by score (descending)
    channelScores.sort((a, b) => b.score - a.score);

    devLog('Suggested: Final ranking:', channelScores.map(c => ({
      channel: c.channel,
      score: c.score
    })));

    // 5. Minimum dominance rule: Top channel must have at least 20% higher score than second place
    const topChannel = channelScores[0];
    const secondChannel = channelScores[1];
    
    let useGenericFallback = false;
    let dominantChannel = null;

    if (!topChannel) {
      devLog('Suggested: No channels to score');
      return null;
    }

    if (secondChannel && topChannel.score > 0) {
      const dominanceRatio = topChannel.score / secondChannel.score;
      const requiredRatio = 1.2; // 20% higher = 1.2x
      
      if (dominanceRatio < requiredRatio) {
        useGenericFallback = true;
        devLog(`Suggested: Top channel ${topChannel.channel} (score: ${topChannel.score}) does not dominate second ${secondChannel.channel} (score: ${secondChannel.score}). Ratio: ${dominanceRatio.toFixed(2)}, required: ${requiredRatio}. Using generic fallback.`);
      } else {
        dominantChannel = topChannel.channel;
        devLog(`Suggested: Top channel ${topChannel.channel} dominates with score ${topChannel.score} (ratio: ${dominanceRatio.toFixed(2)})`);
      }
    } else {
      // Only one channel or second place has score 0
      dominantChannel = topChannel.channel;
      devLog(`Suggested: Single dominant channel ${topChannel.channel} with score ${topChannel.score}`);
    }

    // Generate card
    let cardId;
    let title, description;
    let microInsight;
    
    if (useGenericFallback) {
      cardId = 'focus-suggestion-generic';
      title = 'Clarify what you want to focus on';
      description = 'You are collecting content across multiple places. Choose one direction and Swirl will help shape it.';
      microInsight = 'Your attention is split across channels. Swirl can help you choose one to commit to.';
      devLog('Suggested: Returning generic cross-channel suggestion');
    } else {
      cardId = `focus-suggestion-${dominantChannel?.toLowerCase() || 'default'}`;
      const topChannelData = channelData[dominantChannel];
      const topChannelScore = channelScores.find(c => c.channel === dominantChannel);
      const signals = {
        last_7d: topChannelData?.last_7d || 0,
        last_3d: topChannelData?.last_3d || 0,
        total: topChannelData?.total || 0
      };
      const copy = getSuggestedFocusCopy(dominantChannel, signals);
      title = copy.title;
      description = copy.description;
      
      // Generate microInsight for Suggested Focus
      if (topChannelScore?.breakdown.clusterBonus > 0) {
        microInsight = 'You had a spike in saves here recently. That is a strong signal to work from.';
      } else if (topChannelData?.last_3d > 0 && topChannelData.last_3d >= (topChannelData.last_7d || 0) * 0.7) {
        microInsight = 'This channel has been active in the last few days. It is a good candidate for a short sprint.';
      } else if (topChannelData?.total > 10 && topChannelData.last_7d === 0) {
        microInsight = 'You have built a big backlog here. We can mine it for one clean focus.';
      } else if (topChannelData?.last_24h > 0) {
        microInsight = 'You had a spike in saves here recently. That is a strong signal to work from.';
      } else {
        microInsight = 'This channel has been active in the last few days. It is a good candidate for a short sprint.';
      }
      
      devLog(`Suggested: Returning category-specific suggestion for ${dominantChannel}`);
    }

    const card = {
      id: cardId,
      type: 'focus-suggestion',
      title,
      description,
      microInsight,
      ctaLabel: 'Open Swirlspace',
      ctaAction: () => {
        if (navigateFn) {
          navigateFn('/');
        } else {
          window.location.href = '/';
        }
      },
      gradient: 'from-purple-400 to-pink-400',
      icon: React.createElement(Sparkles, { size: 16, className: 'text-purple-500' }),
    };
    devLog('Suggested card:', card);
    return card;
  } catch (error) {
    console.error('[FocusEngine] Suggested pipeline error', error);
    return null;
  }
}

/**
 * Pipeline 3: Insight Engine
 * Analyzes user behavior patterns to provide meta insights
 */
async function getInsightCard(userId, navigateFn) {
  try {
    console.log('[FocusEngine] Insight pipeline start', { userId });
    const now = new Date();
    const threeDaysAgo = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

    // Count uploads in last 3 days
    const { data: instagramRecent3, error: instagramError3 } = await supabase
      .schema('public')
      .from('instagram_content')
      .select('id, created_at')
      .eq('user_id', userId)
      .gte('created_at', threeDaysAgo.toISOString());

    const { data: deepsearchRecent3, error: deepsearchError3 } = await supabase
      .schema('deepsearch')
      .from('items')
      .select('id, created_at')
      .eq('user_id', userId)
      .gte('created_at', threeDaysAgo.toISOString());

    const uploads_last_3 = (instagramRecent3?.length || 0) + (deepsearchRecent3?.length || 0);

    // Count uploads in last 7 days
    const { data: instagramRecent, error: instagramError } = await supabase
      .schema('public')
      .from('instagram_content')
      .select('id, created_at')
      .eq('user_id', userId)
      .gte('created_at', sevenDaysAgo.toISOString());

    const { data: deepsearchRecent, error: deepsearchError } = await supabase
      .schema('deepsearch')
      .from('items')
      .select('id, created_at')
      .eq('user_id', userId)
      .gte('created_at', sevenDaysAgo.toISOString());

    if (instagramError) {
      console.error('Focus engine error pipeline 3 (instagram uploads query):', instagramError);
    }
    if (deepsearchError) {
      console.error('Focus engine error pipeline 3 (deepsearch uploads query):', deepsearchError);
    }

    const uploads_last_7 = (instagramRecent?.length || 0) + (deepsearchRecent?.length || 0);

    // Count uploads in previous 7 days (days 8-14)
    const { data: instagramPrev, error: instagramPrevError } = await supabase
      .schema('public')
      .from('instagram_content')
      .select('id, created_at')
      .eq('user_id', userId)
      .gte('created_at', fourteenDaysAgo.toISOString())
      .lt('created_at', sevenDaysAgo.toISOString());

    const { data: deepsearchPrev, error: deepsearchPrevError } = await supabase
      .schema('deepsearch')
      .from('items')
      .select('id, created_at')
      .eq('user_id', userId)
      .gte('created_at', fourteenDaysAgo.toISOString())
      .lt('created_at', sevenDaysAgo.toISOString());

    if (instagramPrevError) {
      console.error('Focus engine error pipeline 3 (instagram prev uploads query):', instagramPrevError);
    }
    if (deepsearchPrevError) {
      console.error('Focus engine error pipeline 3 (deepsearch prev uploads query):', deepsearchPrevError);
    }

    const uploads_prev_7 = (instagramPrev?.length || 0) + (deepsearchPrev?.length || 0);

    // Count new conversations started in last 3 days
    const { data: conversationsRecent3, error: conversationsError3 } = await supabase
      .schema('public')
      .from('conversations')
      .select('id, created_at')
      .eq('user_id', userId)
      .gte('created_at', threeDaysAgo.toISOString());

    const chats_last_3 = conversationsRecent3?.length || 0;

    // Count new conversations started in last 7 days
    const { data: conversationsRecent, error: conversationsError } = await supabase
      .schema('public')
      .from('conversations')
      .select('id, created_at')
      .eq('user_id', userId)
      .gte('created_at', sevenDaysAgo.toISOString());

    if (conversationsError) {
      console.error('Focus engine error pipeline 3 (conversations query):', conversationsError);
    }

    const chats_last_7 = conversationsRecent?.length || 0;

    // Count chat messages in last 7 days
    const { data: messagesRecent, error: messagesError } = await supabase
      .from('chat_messages')
      .select('id, created_at')
      .eq('user_id', userId)
      .gte('created_at', sevenDaysAgo.toISOString());

    if (messagesError) {
      console.error('Focus engine error pipeline 3 (messages query):', messagesError);
    }

    const chat_messages_last_7 = messagesRecent?.length || 0;

    console.log('[FocusEngine] Insight metrics', { 
      uploadsLast3: uploads_last_3,
      uploadsLast7: uploads_last_7, 
      uploadsPrev7: uploads_prev_7,
      chatsLast3: chats_last_3,
      chatsLast7: chats_last_7, 
      messagesLast7: chat_messages_last_7 
    });

    // Determine user state (tuned thresholds)
    let stateKey;
    
    // Collector = uploads_last_7 >= 15 and chats_last_7 <= 2
    if (uploads_last_7 >= 15 && chats_last_7 <= 2) {
      stateKey = 'Collector';
    } 
    // Thinker = chats_last_7 >= 5 regardless of uploads
    else if (chats_last_7 >= 5) {
      stateKey = 'Thinker';
    } 
    // Quiet = everything else
    else {
      stateKey = 'Quiet';
    }

    // Get copy from helper
    const metrics = {
      uploadsLast3: uploads_last_3,
      uploadsLast7: uploads_last_7,
      uploadsPrev7: uploads_prev_7,
      chatsLast3: chats_last_3,
      chatsLast7: chats_last_7,
      messagesLast7: chat_messages_last_7
    };
    
    const { title, description } = getInsightCopy(stateKey, metrics);

    // Generate microInsight for Insight card
    let microInsight;
    if (stateKey === 'Collector') {
      microInsight = `Last 7 days: ${uploads_last_7} uploads, ${chats_last_7} new chat${chats_last_7 !== 1 ? 's' : ''}.`;
    } else if (stateKey === 'Thinker') {
      microInsight = `Last 7 days: ${chats_last_7} chat${chats_last_7 !== 1 ? 's' : ''}, ${chat_messages_last_7} message${chat_messages_last_7 !== 1 ? 's' : ''}.`;
    } else {
      // Quiet mode
      microInsight = 'Very little activity in the last week. Even 3 new uploads or one real question would give Swirl more signal.';
    }

    console.log('[FocusEngine] Insight state', stateKey);

    const card = {
      id: 'insight-user-patterns',
      type: 'insight',
      title,
      description,
      microInsight,
      ctaLabel: 'See insights',
      ctaAction: () => {
        if (navigateFn) {
          navigateFn('/insights');
        } else {
          window.location.href = '/insights';
        }
      },
      gradient: 'from-pink-400 to-rose-400',
      icon: React.createElement(User, { size: 16, className: 'text-purple-500' }),
    };
    devLog('Insight card:', card);
    return card;
  } catch (error) {
    console.error('[FocusEngine] Insight pipeline error', error);
    return null;
  }
}

/**
 * Fallback cards (used when all pipelines fail)
 */
function getFallbackCards(navigateFn) {
  return [
    {
      id: 'active-focus-1',
      type: 'active-focus',
      title: 'Plan the NYC trip',
      description: 'Ask Swirl to pull everything you saved about NYC and turn it into a plan.',
      ctaLabel: 'Open NYC plan',
      ctaAction: () => {
        if (navigateFn) {
          navigateFn('/ai-chat');
        } else {
          window.location.href = '/ai-chat';
        }
      },
      gradient: 'from-blue-400 to-purple-400',
      icon: React.createElement(Target, { size: 16, className: 'text-purple-500' }),
    },
    {
      id: 'focus-suggestion-1',
      type: 'focus-suggestion',
      title: 'Tighten your sales story',
      description: 'Ask Swirl to use your saved sales threads and hooks to sharpen your pitch.',
      ctaLabel: 'Use my sales content',
      ctaAction: () => {
        if (navigateFn) {
          navigateFn('/');
        } else {
          window.location.href = '/';
        }
      },
      gradient: 'from-purple-400 to-pink-400',
      icon: React.createElement(Sparkles, { size: 16, className: 'text-purple-500' }),
    },
    {
      id: 'insight-1',
      type: 'insight',
      title: 'Clear your head',
      description: "Ask Swirl what you've saved recently and what you should focus on next.",
      ctaLabel: 'Ask for a weekly debrief',
      ctaAction: () => {
        if (navigateFn) {
          navigateFn('/ai-chat');
        } else {
          window.location.href = '/ai-chat';
        }
      },
      gradient: 'from-pink-400 to-rose-400',
      icon: React.createElement(User, { size: 16, className: 'text-purple-500' }),
    },
  ];
}

// Track if engine is currently running to prevent concurrent executions
let isEngineRunning = false;

/**
 * Main intelligence engine function
 * Runs all three pipelines in parallel and returns focus cards
 * 
 * @param {string} userId - User ID
 * @param {Function} navigateFn - Optional navigation function (from React Router)
 * @returns {Promise<FocusCard[]>} Array of focus cards
 */
export async function getSmartFocusCardsForUser(userId, navigateFn = null) {
  console.log('[FocusEngine] start', { userId });
  
  // Protection: Skip if already running (for realtime refreshes)
  if (isEngineRunning) {
    console.log('[FocusEngine] Skipping, still running');
    return null;
  }

  if (!userId) {
    console.error('Focus engine: userId is required');
    return getFallbackCards(navigateFn);
  }

  // Set running flag
  isEngineRunning = true;

  try {
    // PART 2: Load or refresh user memory
    let memory = await loadUserMemory(userId);
    
    // If memory doesn't exist or is stale (older than 1 hour), refresh it
    const shouldRefreshMemory = !memory || (memory.computed_at && 
      (new Date() - new Date(memory.computed_at)) > 60 * 60 * 1000);
    
    if (shouldRefreshMemory) {
      console.log('[FocusEngine] Refreshing user memory');
      memory = await refreshUserMemory(userId);
    } else {
      console.log('[FocusEngine] Using cached memory');
    }

    // Run all three pipelines in parallel
    // Note: Memory is available but pipelines don't use it yet (future enhancement)
    const results = await Promise.allSettled([
      getActiveFocusCard(userId, navigateFn),
      getSuggestedFocusCard(userId, navigateFn),
      getInsightCard(userId, navigateFn),
    ]);

    devLog('Pipeline results:', results);

    const activeCard = results[0].status === 'fulfilled' ? results[0].value : null;
    const suggestedCard = results[1].status === 'fulfilled' ? results[1].value : null;
    const insightCard = results[2].status === 'fulfilled' ? results[2].value : null;

    console.log('[FocusEngine] pipeline results', { activeCard, suggestedCard, insightCard });

    // PART 3: Cross-pipeline reasoning
    const pipelineResults = { activeCard, suggestedCard, insightCard };
    const { crossInsight } = analyzeAndEnrich(memory, pipelineResults);

    // Extract results, filtering out nulls and errors, and enrich with crossInsight
    const cards = [];
    
    if (activeCard) {
      const enriched = enrichCardWithCrossInsight(activeCard, crossInsight);
      cards.push(enriched);
      if (crossInsight) {
        console.log('[CrossReasoner] injected crossInsight into card active-focus');
      }
    }
    
    if (suggestedCard) {
      const enriched = enrichCardWithCrossInsight(suggestedCard, crossInsight);
      cards.push(enriched);
      if (crossInsight) {
        console.log('[CrossReasoner] injected crossInsight into card focus-suggestion');
      }
    }
    
    if (insightCard) {
      const enriched = enrichCardWithCrossInsight(insightCard, crossInsight);
      cards.push(enriched);
      if (crossInsight) {
        console.log('[CrossReasoner] injected crossInsight into card insight');
      }
    }

    // If we got at least one card, return them
    if (cards.length > 0) {
      console.log('[FocusEngine] final cards', cards);
      return cards;
    }

    // If all pipelines failed, return fallback cards
    console.warn('Focus engine: All pipelines failed, using fallback cards');
    const fallbackCards = getFallbackCards(navigateFn);
    console.log('[FocusEngine] final cards', fallbackCards);
    return fallbackCards;
  } catch (error) {
    console.error('[FocusEngine] error in main engine', error);
    return getFallbackCards(navigateFn);
  } finally {
    // Always clear running flag
    isEngineRunning = false;
  }
}

